import { Component, computed, input, signal } from '@angular/core';

interface StructuredEntry {
  key: string;
  value: string;
}

interface ScalarSection {
  key: string;
  entries: StructuredEntry[];
}

interface CollectionTable {
  key: string;
  columns: string[];
  rows: Array<Record<string, string>>;
  itemCount: number;
}

interface PreviewData {
  scalarSections: ScalarSection[];
  collections: CollectionTable[];
}

type PreviewMode = 'structured' | 'json';

interface PreviewModeOption {
  label: string;
  value: PreviewMode;
}

const getPropertyName = (path: string): string => {
  const pathSegments = path.split('.');
  return pathSegments[pathSegments.length - 1] || path;
};

const formatPropertyLabel = (path: string): string => {
  const propertyName = getPropertyName(path);
  const normalizedLabel = propertyName
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim();

  if (normalizedLabel.length === 0) {
    return path;
  }

  return normalizedLabel.charAt(0).toUpperCase() + normalizedLabel.slice(1);
};

const isObject = (value: unknown): boolean => value !== null && typeof value === 'object' && !Array.isArray(value);

const isPrimitive = (value: unknown): boolean => {
  const valueType = typeof value;
  return valueType === 'string' || valueType === 'number' || valueType === 'boolean';
};

const toDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return 'null';
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value.toString() : 'NaN';
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  const asString = String(value);
  if (asString.length > 50) {
    return `${asString.slice(0, 47)}...`;
  }

  return asString;
};

const normalizeCollectionRow = (item: unknown): Record<string, unknown> => {
  if (isObject(item)) {
    return item as Record<string, unknown>;
  }

  return { value: item };
};

const toCollectionTable = (items: unknown[], key: string): CollectionTable => {
  const normalizedRows = items.map(item => normalizeCollectionRow(item));
  const columns = Array.from(
    normalizedRows.reduce((set, row) => {
      Object.keys(row).forEach(column => set.add(column));
      return set;
    }, new Set<string>())
  );

  const safeColumns = columns.length > 0 ? columns : ['value'];
  const rows = normalizedRows.map(row =>
    safeColumns.reduce<Record<string, string>>((renderedRow, column) => {
      renderedRow[column] = toDisplayValue(row[column]);
      return renderedRow;
    }, {})
  );

  return {
    key,
    columns: safeColumns,
    rows,
    itemCount: items.length
  };
};

const toJsonPreview = (value: unknown): string => {
  if (value === null || value === undefined) {
    return 'null';
  }

  const visitedObjects = new WeakSet<object>();

  const circularSafeStringify = (input: unknown): string => JSON.stringify(
    input,
    (_key, nextValue) => {
      if (typeof nextValue === 'object' && nextValue !== null) {
        if (visitedObjects.has(nextValue)) {
          return '[Circular]';
        }
        visitedObjects.add(nextValue);
      }

      return nextValue;
    },
    2
  );

  try {
    return circularSafeStringify(value) ?? String(value);
  } catch {
    return String(value);
  }
};

const collectNestedObjectEntries = (
  value: Record<string, unknown>,
  relativePrefix: string,
  collectionPrefix: string
): { entries: StructuredEntry[]; collections: CollectionTable[] } => {
  return Object.keys(value).reduce<{ entries: StructuredEntry[]; collections: CollectionTable[] }>((result, key) => {
    const nestedValue = value[key];
    const nestedPath = relativePrefix ? `${relativePrefix}.${key}` : key;

    if (Array.isArray(nestedValue)) {
      return {
        entries: result.entries,
        collections: [...result.collections, toCollectionTable(nestedValue, `${collectionPrefix}.${nestedPath}`)]
      };
    }

    if (isObject(nestedValue)) {
      const nestedResult = collectNestedObjectEntries(
        nestedValue as Record<string, unknown>,
        nestedPath,
        collectionPrefix
      );

      return {
        entries: [...result.entries, ...nestedResult.entries],
        collections: [...result.collections, ...nestedResult.collections]
      };
    }

    return {
      entries: [...result.entries, { key: nestedPath, value: toDisplayValue(nestedValue) }],
      collections: result.collections
    };
  }, { entries: [], collections: [] });
};

const collectPreviewData = (value: unknown, prefix = ''): PreviewData => {
  if (value === null || value === undefined) {
    return {
      scalarSections: [{ key: prefix || 'General', entries: [{ key: 'value', value: 'null' }] }],
      collections: []
    };
  }

  if (isPrimitive(value)) {
    return {
      scalarSections: [{ key: prefix || 'General', entries: [{ key: 'value', value: toDisplayValue(value) }] }],
      collections: []
    };
  }

  if (Array.isArray(value)) {
    return {
      scalarSections: [],
      collections: [toCollectionTable(value, prefix || 'value')]
    };
  }

  const objectValue = value as Record<string, unknown>;
  const keys = Object.keys(objectValue);

  if (keys.length === 0) {
    return {
      scalarSections: [{ key: prefix || 'General', entries: [{ key: 'value', value: '{}' }] }],
      collections: []
    };
  }

  const reduced = keys.reduce<{ scalarSections: ScalarSection[]; generalEntries: StructuredEntry[]; collections: CollectionTable[] }>((result, key) => {
    const nextPrefix = prefix ? `${prefix}.${key}` : key;
    const currentValue = objectValue[key];

    if (Array.isArray(currentValue)) {
      return {
        scalarSections: result.scalarSections,
        generalEntries: result.generalEntries,
        collections: [...result.collections, toCollectionTable(currentValue, nextPrefix)]
      };
    }

    if (isObject(currentValue)) {
      const nestedResult = collectNestedObjectEntries(currentValue as Record<string, unknown>, '', nextPrefix);
      const nextSections = nestedResult.entries.length > 0
        ? [...result.scalarSections, { key: nextPrefix, entries: nestedResult.entries }]
        : result.scalarSections;

      return {
        scalarSections: nextSections,
        generalEntries: result.generalEntries,
        collections: [...result.collections, ...nestedResult.collections]
      };
    }

    return {
      scalarSections: result.scalarSections,
      generalEntries: [...result.generalEntries, { key, value: toDisplayValue(currentValue) }],
      collections: result.collections
    };
  }, { scalarSections: [], generalEntries: [], collections: [] });

  return {
    scalarSections: reduced.generalEntries.length > 0
      ? [{ key: prefix || 'General', entries: reduced.generalEntries }, ...reduced.scalarSections]
      : reduced.scalarSections,
    collections: reduced.collections
  };
};

@Component({
  selector: 'app-catalog-item-preview',
  templateUrl: './catalog-item-preview.component.html',
  standalone: false
})
export class CatalogItemPreviewComponent {
  public item = input<unknown>(null);
  public title = input('Catalog Item Details');
  public buttonTooltip = input('View catalog details');

  public isDialogVisible = signal(false);
  public viewMode = signal<PreviewMode>('structured');
  public previewModeOptions: PreviewModeOption[] = [
    { label: 'Structured', value: 'structured' },
    { label: 'JSON', value: 'json' }
  ];
  public hasData = computed(() => this.item() !== null && this.item() !== undefined);
  public readonly formatPropertyLabel = formatPropertyLabel;

  private previewData = computed<PreviewData>(() => {
    const itemValue = this.item();
    if (itemValue === null || itemValue === undefined) {
      return { scalarSections: [], collections: [] };
    }
    return collectPreviewData(itemValue);
  });

  public scalarSections = computed(() => this.previewData().scalarSections);
  public structuredEntries = computed(() => this.scalarSections().flatMap(section => section.entries));
  public collectionTables = computed(() => this.previewData().collections);
  public jsonPreview = computed(() => toJsonPreview(this.item()));
}
